import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, Grid, Title } from "@tremor/react";
import { Button, Descriptions, Dropdown, Empty, Skeleton, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";

import { CollectionsTaskStatus, type CollectionsTask } from "@fitness-app/data-models/entities/CollectionsCase";

import ConfirmPaymentModal from "~/modules/Products/Product/ProductOrders/ConfirmPaymentModal";
import { ClientPackageDrawer } from "~/modules/Trainee/TraineeProfile/components/ClientPackageDrawer/ClientPackageDrawer";
import {
  useConfirmManualPayment,
  type ConfirmPaymentDocumentIssuance,
} from "~/modules/Products/hooks/useConfirmManualPayment";

import {
  CaseStatusTag,
  formatPln,
  KPI_GRID,
  pluralRaty,
  SituationTag,
  stepLabel,
  taskLabel,
} from "./collectionsLabels";
import AssistantDrawer from "./components/AssistantDrawer";
import CaseActionModal, { type CaseActionKind } from "./components/CaseActionModal";
import CaseNotes from "./components/CaseNotes";
import CaseTimeline from "./components/CaseTimeline";
import CompleteTaskModal from "./components/CompleteTaskModal";
import {
  useCollectionsCase,
  useCompleteCollectionsTask,
  useDownloadCollectionsLetter,
  useRecordCollectionsAction,
  type CollectionsInvoice,
} from "./hooks/useCollections";

const SourceTag = ({ invoice }: { invoice: CollectionsInvoice }) => {
  if (invoice.source === "manual_invoice") {
    return <Tag color="orange">Przelew</Tag>;
  }
  if (invoice.source === "manual_upcoming") {
    return <Tag>Przelew – przed terminem</Tag>;
  }
  if (invoice.source === "card_retrying") {
    return <Tag color="gold">Karta – ponawiana</Tag>;
  }
  return <Tag color="red">Karta – wyczerpana</Tag>;
};

/**
 * The case card from the brief: in five seconds — how much she owes, how much
 * is still ahead, what we did, what she said, where the case is, what to do now.
 */
const CollectionsCasePage = () => {
  const { caseId } = useParams<{ caseId: string }>();
  // Back to the list the case was opened from, with its filters and page; a direct link lands on the tab.
  const back = (useLocation().state as { back?: string } | null)?.back;
  const backTo = back?.startsWith("/products") ? back : "/products?tab=collections";
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useCollectionsCase(caseId);
  const completeTask = useCompleteCollectionsTask();
  const recordAction = useRecordCollectionsAction();
  const confirmPayment = useConfirmManualPayment();
  const downloadLetter = useDownloadCollectionsLetter();

  const [assistantOpen, setAssistantOpen] = useState(false);
  // The contract's access record, opened the way the client profile does it.
  const [openedProductId, setOpenedProductId] = useState<string | null>(null);
  const [taskToClose, setTaskToClose] = useState<CollectionsTask | null>(null);
  const [action, setAction] = useState<CaseActionKind | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<{ invoice: CollectionsInvoice; clientEmail: string } | null>(null);

  if (isError) {
    return <Empty description="Nie udało się wczytać sprawy" className="py-10" />;
  }

  if (isLoading || !data) {
    return <Skeleton active paragraph={{ rows: 10 }} className="p-4" />;
  }

  const { case: current, contracts, events, tasks } = data;
  const openTasks = tasks.filter((task) => task.status === CollectionsTaskStatus.Open);
  const today = dayjs().format("YYYY-MM-DD");
  const nextTask = openTasks[0] ?? null;

  const handleConfirmPayment = async (documentIssuance?: ConfirmPaymentDocumentIssuance) => {
    if (!paymentTarget) {
      return;
    }
    try {
      await confirmPayment.mutateAsync({
        productId: paymentTarget.invoice.productId,
        orderId: paymentTarget.invoice.orderId,
        clientEmail: paymentTarget.clientEmail,
        documentIssuance,
      });
      void queryClient.invalidateQueries(["collections"]);
    } finally {
      setPaymentTarget(null);
    }
  };

  const actionItems = [
    { key: "declaration", label: "Ustal deklarację płatności" },
    { key: "settlement", label: "Ustal ugodę" },
    { key: "situation", label: "Sytuacja klientki" },
    { key: "status", label: "Zmień status" },
    { key: "agency", label: "Przekaż do windykacji zewnętrznej" },
    { key: "note", label: "Dodaj notatkę" },
    { type: "divider" as const },
    { key: "letter:demand", label: "Pobierz wezwanie do zapłaty (PDF)" },
    { key: "letter:final", label: "Pobierz ostateczne wezwanie (PDF)" },
  ];

  const handleAction = (key: string) => {
    if (key.startsWith("letter:")) {
      downloadLetter.mutate({ caseId: current.id, kind: key.endsWith("final") ? "final" : "demand" });
      return;
    }
    setAction(key as CaseActionKind);
  };

  const invoiceColumns = [
    { title: "Rata", dataIndex: "paymentCycle", key: "cycle", render: (cycle: number | null) => cycle ?? "—" },
    { title: "Kwota", dataIndex: "amount", key: "amount", render: (amount: number) => formatPln(amount) },
    { title: "Termin", dataIndex: "dueAt", key: "dueAt", render: (dueAt: string) => dayjs(dueAt).format("DD.MM.YYYY") },
    {
      title: "Źródło",
      key: "source",
      render: (_: unknown, invoice: CollectionsInvoice) => <SourceTag invoice={invoice} />,
    },
    {
      title: "Faktura",
      dataIndex: "invoiceId",
      key: "invoiceId",
      render: (invoiceId: string) => (
        <a href={`https://dashboard.stripe.com/invoices/${invoiceId}`} target="_blank" rel="noreferrer">
          Stripe
        </a>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, invoice: CollectionsInvoice) =>
        invoice.source === "card_retrying" ? null : (
          <Tooltip title="Przelew na konto, Tpay, Fakturownia — faktura w Stripe zostanie oznaczona jako opłacona poza systemem">
            <Button
              size="small"
              type="link"
              onClick={() => setPaymentTarget({ invoice, clientEmail: current.clientEmail })}
            >
              Oznacz jako opłacone
            </Button>
          </Tooltip>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-6 p-4">
      <div>
        <Link to={backTo} className="text-sm text-gray-500">
          ← Należności
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <Typography.Title level={3} className="mb-0">
            {data.clientName ?? current.clientEmail}
          </Typography.Title>
          <CaseStatusTag status={current.status} />
          <SituationTag situation={current.situation} />
          {current.balance.hasArchivedContract ? <Tag>Zarchiwizowana</Tag> : null}
        </div>
        <Typography.Text type="secondary">
          {current.clientEmail}
          {data.clientPhone ? ` · ${data.clientPhone}` : ""} · sprawa od {dayjs(current.openedAt).format("DD.MM.YYYY")}
        </Typography.Text>
      </div>

      <div className={KPI_GRID}>
        <Card>
          <Statistic
            title="Zaległość wymagalna"
            value={formatPln(current.balance.overdueAmount)}
            valueStyle={{ color: current.balance.overdueAmount ? "#e11d48" : undefined }}
          />
          <p className="mt-1 text-xs text-gray-500">
            {current.balance.overdueInvoices} {pluralRaty(current.balance.overdueInvoices)} po terminie
          </p>
        </Card>
        <Card>
          <Statistic title="Przyszłe zobowiązania" value={formatPln(current.balance.futureAmount)} />
          <p className="mt-1 text-xs text-gray-500">
            {current.balance.futureInstallments} {pluralRaty(current.balance.futureInstallments)} przed nią
          </p>
        </Card>
        <Card>
          <Statistic title="Pozostała wartość kontraktu" value={formatPln(current.balance.remainingContractAmount)} />
        </Card>
        <Card>
          <Statistic
            title="Dni po terminie"
            value={current.balance.daysOverdue}
            valueStyle={{ color: current.balance.daysOverdue > 30 ? "#e11d48" : undefined }}
          />
        </Card>
        <Card>
          <Statistic
            title="Odzyskane w sprawie"
            value={formatPln(data.recoveredAmount)}
            valueStyle={{ color: "#059669" }}
          />
        </Card>
      </div>

      <Card className={nextTask && nextTask.dueAt <= today ? "border-orange-400" : ""}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Typography.Text type="secondary" className="text-xs uppercase tracking-wide">
              Następna akcja
            </Typography.Text>
            <Title>
              {nextTask
                ? `${nextTask.dueAt <= today ? "DZISIAJ: " : `${dayjs(nextTask.dueAt).format("DD.MM")}: `}${taskLabel(nextTask.type)}`
                : "Brak otwartych zadań"}
            </Title>
            {current.declaration?.outcome === "pending" ? (
              <Typography.Text type="secondary">
                Deklaracja: {formatPln(current.declaration.amount)} do{" "}
                {dayjs(current.declaration.dueAt).format("DD.MM.YYYY")}
              </Typography.Text>
            ) : null}
            {current.settlement?.status === "active" ? (
              <Typography.Text type="secondary">
                Ugoda: {current.settlement.installments} × {formatPln(current.settlement.amount)}
              </Typography.Text>
            ) : null}
            {current.externalCollection ? (
              <Typography.Text type="secondary">
                {current.externalCollection.agency} od {dayjs(current.externalCollection.handedAt).format("DD.MM.YYYY")}
                {current.externalCollection.reference ? ` · ${current.externalCollection.reference}` : ""}
              </Typography.Text>
            ) : null}
            {current.automation ? (
              <div className="mt-1">
                <Typography.Text type="secondary">
                  Automatyczne wiadomości od {dayjs(current.automation.startedAt).format("DD.MM.YYYY")} (
                  {current.automation.ladder === "card" ? "raty kartą" : "raty przelewem"}):{" "}
                  {Object.entries(current.automation.steps).length
                    ? Object.entries(current.automation.steps)
                        .map(([step, date]) => `${stepLabel(step)} — ${dayjs(date).format("DD.MM")}`)
                        .join(", ")
                    : "jeszcze nic nie wyszło"}
                </Typography.Text>
              </div>
            ) : null}
          </div>
          <div className="flex gap-2">
            {nextTask ? (
              <Button type="primary" onClick={() => setTaskToClose(nextTask)}>
                Wykonano
              </Button>
            ) : null}
            <Button onClick={() => setAssistantOpen(true)}>Asystent AI</Button>
            <Dropdown menu={{ items: actionItems, onClick: ({ key }) => handleAction(key) }}>
              <Button loading={downloadLetter.isLoading}>Akcje</Button>
            </Dropdown>
          </div>
        </div>
        {openTasks.length > 1 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {openTasks.slice(1).map((task) => (
              <Tag key={task.id} onClick={() => setTaskToClose(task)} className="cursor-pointer">
                {taskLabel(task.type)} · {dayjs(task.dueAt).format("DD.MM")}
              </Tag>
            ))}
          </div>
        ) : null}
      </Card>

      <Grid numItemsLg={2} className="gap-6">
        <div className="flex flex-col gap-6">
          {contracts.map((contract, index) => (
            <Card key={contract.id}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Title>
                  Kontrakt {index + 1}: {contract.productName ?? contract.productId}
                </Title>
                <Button type="link" onClick={() => setOpenedProductId(contract.productId)}>
                  Szczegóły pakietu
                </Button>
              </div>
              <Descriptions size="small" column={2} className="mt-2">
                <Descriptions.Item label="Plan">
                  {contract.installments ? `${contract.installments} × ${formatPln(contract.installmentAmount)}` : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Wystawione raty">{contract.cycleNumber ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Koniec rat">
                  {contract.installmentEndsAt ? dayjs(contract.installmentEndsAt).format("DD.MM.YYYY") : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Dostęp">
                  {contract.clientStatus === "archived" ? "zarchiwizowany" : "aktywny"}
                </Descriptions.Item>
              </Descriptions>
              {contract.invoices.length ? (
                <Table
                  className="mt-3"
                  size="small"
                  rowKey="orderId"
                  columns={invoiceColumns}
                  dataSource={contract.invoices}
                  pagination={false}
                  scroll={{ x: true }}
                />
              ) : (
                <Typography.Text type="secondary">Brak nieopłaconych rat w tym kontrakcie.</Typography.Text>
              )}
            </Card>
          ))}
          <CaseNotes
            events={events}
            saving={recordAction.isLoading}
            onAdd={(note, done) =>
              recordAction.mutate({ caseId: current.id, action: { type: "note", note } }, { onSuccess: done })
            }
          />
        </div>

        <Card>
          <Title>Oś czasu</Title>
          <div className="mt-4">
            {events.length || openTasks.length ? (
              <CaseTimeline events={events} openTasks={openTasks} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Jeszcze nic się nie wydarzyło" />
            )}
          </div>
        </Card>
      </Grid>

      <CompleteTaskModal
        task={taskToClose}
        loading={completeTask.isLoading}
        onCancel={() => setTaskToClose(null)}
        onSubmit={(values) =>
          taskToClose &&
          completeTask.mutate(
            { caseId: current.id, taskId: taskToClose.id, ...values },
            { onSuccess: () => setTaskToClose(null) },
          )
        }
      />
      <CaseActionModal
        kind={action}
        current={current}
        loading={recordAction.isLoading}
        onCancel={() => setAction(null)}
        onSubmit={(payload) =>
          recordAction.mutate({ caseId: current.id, action: payload }, { onSuccess: () => setAction(null) })
        }
      />
      <ClientPackageDrawer
        email={openedProductId ? current.clientEmail : null}
        productId={openedProductId}
        onClose={() => setOpenedProductId(null)}
      />
      <AssistantDrawer
        open={assistantOpen}
        caseId={current.id}
        clientPhone={data.clientPhone}
        onClose={() => setAssistantOpen(false)}
      />
      <ConfirmPaymentModal
        open={paymentTarget !== null}
        loading={confirmPayment.isLoading}
        onCancel={() => setPaymentTarget(null)}
        onConfirm={(documentIssuance) => void handleConfirmPayment(documentIssuance)}
      />
    </div>
  );
};

export default CollectionsCasePage;
